import React, { createContext, useContext, useState, useEffect } from 'react';

export type ViewMode = 'card' | 'list';

interface ViewModeContextType {
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    toggleViewMode: () => void;
}

const STORAGE_KEY = 'viewMode';

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const ViewModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [viewMode, setViewModeState] = useState<ViewMode>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        return (saved === 'list' ? 'list' : 'card') as ViewMode;
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, viewMode);
    }, [viewMode]);

    const setViewMode = (mode: ViewMode) => setViewModeState(mode);

    const toggleViewMode = () => {
        setViewModeState(prev => prev === 'card' ? 'list' : 'card');
    };

    return (
        <ViewModeContext.Provider value={{ viewMode, setViewMode, toggleViewMode }}>
            {children}
        </ViewModeContext.Provider>
    );
};

export const useViewMode = () => {
    const context = useContext(ViewModeContext);
    if (context === undefined) {
        throw new Error('useViewMode must be used within a ViewModeProvider');
    }
    return context;
};
